import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { useSEO } from "@/hooks/useSEO";

const HEADLINES = {
  men: { title: "MEN", tagline: "Confidence is the best print." },
  women: { title: "WOMEN", tagline: "Wear your individuality." },
  gifts: { title: "GIFTS", tagline: "Custom-printed. Perfectly gifted." },
};

const SUBS = {
  men: ["tshirts", "shirts", "jeans", "oversized", "sweatshirts", "hoodies", "polo"],
  women: ["tshirts", "shirts", "jeans", "oversized", "sweatshirts", "hoodies", "polo"],
  gifts: ["gifts"],
};

export default function Category({ cat }) {
  const [all, setAll] = useState([]);
  const [subs, setSubs] = useState([]);
  const [priceMax, setPriceMax] = useState(5000);

  const seoMap = {
    men: { title: "Men's T-Shirts, Hoodies & Sweatshirts", desc: "Shop premium men's t-shirts, oversized tees, hoodies, sweatshirts, shirts and jeans at Venvay. Free shipping over ₹1499. Buy 2 Get 1 Free.", kw: "mens tshirts india, mens hoodies india, mens oversized tees, premium mens clothing india, venvay men" },
    women: { title: "Women's T-Shirts, Hoodies & Sweatshirts", desc: "Shop premium women's tees, cropped hoodies, sweatshirts, shirts and jeans at Venvay. Free shipping over ₹1499. Buy 2 Get 1 Free.", kw: "womens tshirts india, womens hoodies india, cropped hoodie india, premium womens clothing india, venvay women" },
    gifts: { title: "Gifts & Signature Boxes", desc: "Curated Venvay gift boxes and hampers — perfect for birthdays, festivals and corporate gifting.", kw: "venvay gift box, corporate gift india, t-shirt gift hamper, festive gift box" },
  };
  useSEO({
    title: seoMap[cat].title,
    description: seoMap[cat].desc,
    keywords: seoMap[cat].kw,
    canonicalPath: `/${cat}`,
  });

  useEffect(() => {
    setSubs([]); setPriceMax(5000);
    api.get("/products", { params: { category: cat } }).then((r) => setAll(r.data));
  }, [cat]);

  const filtered = useMemo(() => {
    return all.filter((p) => (subs.length === 0 || subs.includes(p.subcategory)) && p.price <= priceMax);
  }, [all, subs, priceMax]);

  const toggleSub = (s) => setSubs((cur) => cur.includes(s) ? cur.filter(x => x !== s) : [...cur, s]);

  return (
    <div>
      <div className="bg-[#0F0B08] text-[#F0E5D0] py-16 border-b border-[#D4B25C]/10">
        <div className="max-w-7xl mx-auto px-6">
          <p className="label-tag text-[#D4B25C]/60 mb-4">Collection</p>
          <h1 className="font-display text-6xl sm:text-8xl font-black tracking-tight">
            {HEADLINES[cat].title}<span className="text-[#D4B25C]">.</span>
          </h1>
          <p className="mt-4 text-lg italic text-white/70">{HEADLINES[cat].tagline}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-10">
        <aside className="lg:sticky lg:top-32 lg:self-start space-y-8">
          <div>
            <p className="label-tag mb-4">Category</p>
            <div className="space-y-2">
              {SUBS[cat].map((s) => (
                <label key={s} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox data-testid={`filter-${s}`} checked={subs.includes(s)} onCheckedChange={() => toggleSub(s)} />
                  <span className="text-sm capitalize">{s}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <p className="label-tag mb-4">Max Price: ₹{priceMax}</p>
            <Slider data-testid="price-slider" value={[priceMax]} onValueChange={(v) => setPriceMax(v[0])} min={500} max={5000} step={100} />
          </div>
          <p className="text-xs text-muted-foreground">{filtered.length} products</p>
        </aside>
        <section>
          {filtered.length === 0 ? (
            <p className="text-center py-20 text-muted-foreground">No products match your filters.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {filtered.map((p) => <ProductCard key={p.id} p={p} />)}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
